import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import { Box, Stack } from "@mui/material";
import type { OrganisationImportantReminders } from "adl-gen/ferovinum/app/api";
import type { LoadingValue } from "utils/utility-types";
import { ChartStateWrapper } from "../../../widgets/charts";
import { useOrganisationDashboardPageViewViewModel } from "./use-view-model";
import { ExpiringDealChart } from "./widgets/expiring-deal-chart";
import { InventoryChart } from "./widgets/inventory-chart";
import { SalesChart } from "./widgets/sales-chart";
import { SalesCreditLimitChart } from "./widgets/sales-credit-limit-chart";
import { ToDoList } from "./widgets/to-do-list";

const mediaQuery = "@media (max-width: 1460px)";

const styles = {
  mainStack: { flex: 1, display: "flex", flexDirection: "column" } as const,
  contentStack: { flex: 1, minHeight: 0 } as const,
  columnStack: { flex: 1, minWidth: 0 } as const,
  chartGrid: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: 4,
    [mediaQuery]: {
      gridTemplateColumns: "1fr",
    },
  } as const,
  chartGridWithAlign: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: 4,
    alignItems: "start",
    [mediaQuery]: {
      gridTemplateColumns: "1fr",
      alignItems: "stretch",
    },
  } as const,
  largeChart: {
    minWidth: 500,
    height: 400,
    minHeight: 350,
    maxHeight: 450,
    [mediaQuery]: {
      minWidth: "100%",
    },
  } as const,
  smallChart: {
    minWidth: 250,
    height: 400,
    minHeight: 350,
    maxHeight: 450,
    [mediaQuery]: {
      minWidth: "100%",
    },
  } as const,
  creditLimitChart: {
    minWidth: 250,
    height: 200,
    minHeight: 180,
    maxHeight: 220,
    [mediaQuery]: {
      minWidth: "100%",
      height: 400,
      minHeight: 350,
      maxHeight: 450,
    },
  } as const,
  todoContainer: {
    minWidth: 320,
    maxWidth: 370,
    flex: "0 0 auto",
    display: "flex",
    flexDirection: "column",
    [mediaQuery]: {
      minHeight: "600px",
    },
  } as const,
  todoInner: { flex: 1, display: "flex", flexDirection: "column" } as const,
} as const;

interface Props {
  loadingImportantReminders: LoadingValue<OrganisationImportantReminders>;
}

export const OrganisationDashboardPageView = ({
  loadingImportantReminders,
}: Props) => {
  const {
    organisationCredit,
    currencyFormatter,
    todos,
    inventoryData,
    expiringDealsData,
    salesData,
    greeting,
    salesCreditLimitData,
  } = useOrganisationDashboardPageViewViewModel();

  return (
    <PortalPageContent header={<PortalPageContentHeader title={greeting} />}>
      <Stack spacing={4} sx={styles.mainStack}>
        <Stack direction={"row"} spacing={4} sx={styles.contentStack}>
          <Stack spacing={4} sx={styles.columnStack}>
            <Box sx={styles.chartGrid}>
              <Box sx={styles.largeChart}>
                <ChartStateWrapper
                  isLoading={inventoryData.isLoading}
                  data={inventoryData.data}
                  error={inventoryData.error}
                  renderChart={(data) => (
                    <InventoryChart data={data!} title="Stock Balances" />
                  )}
                  title="Stock Balances"
                  emptyMessage="No inventory data available"
                />
              </Box>
              <Box sx={styles.smallChart}>
                <ChartStateWrapper
                  isLoading={expiringDealsData.isLoading}
                  data={expiringDealsData.data}
                  error={expiringDealsData.error}
                  renderChart={(data) => (
                    <ExpiringDealChart data={data!} title="Expiring Deals" />
                  )}
                  title="Expiring Deals"
                  emptyMessage="No expiring deals data available"
                />
              </Box>
            </Box>
            <Box sx={styles.chartGridWithAlign}>
              <Box sx={styles.largeChart}>
                <ChartStateWrapper
                  isLoading={salesData.isLoading}
                  data={salesData.data}
                  error={salesData.error}
                  renderChart={(data) => (
                    <SalesChart data={data!} title="Sales" />
                  )}
                  title="Sales"
                  emptyMessage="No sales data available"
                />
              </Box>
              {organisationCredit.data && (
                <Box sx={styles.creditLimitChart}>
                  <ChartStateWrapper
                    isLoading={organisationCredit.isLoading}
                    data={salesCreditLimitData}
                    error={organisationCredit.error}
                    renderChart={(data) => (
                      <SalesCreditLimitChart
                        data={data!}
                        title="Sales Credit Limit"
                        currencyFormatter={{
                          format: (val: number) =>
                            currencyFormatter.format(val.toString()),
                        }}
                      />
                    )}
                    title="Sales Credit Limit"
                    emptyMessage="No credit limit data available"
                  />
                </Box>
              )}
            </Box>
          </Stack>

          <Box sx={styles.todoContainer}>
            <Box sx={styles.todoInner}>
              <ToDoList
                loadingImportantReminders={loadingImportantReminders}
                todos={todos}
              />
            </Box>
          </Box>
        </Stack>
      </Stack>
    </PortalPageContent>
  );
};
